import React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Security.css";

const Security = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const navigate = useNavigate();
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long";
    }
    if (!hasUpperCase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasLowerCase) {
      return "Password must contain at least one lowercase letter";
    }
    if (!hasNumber) {
      return "Password must contain at least one number";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character";
    }
    return null;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else {
      const passwordError = validatePassword(newPassword);
      if (passwordError) {
        newErrors.newPassword = passwordError;
      }
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    setTimeout(() => {
      setLoading(false);

      const isSuccess = Math.random() > 0.3;

      if (isSuccess) {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setErrors({});
        setShowSuccessModal(true);
      } else {
        setShowFailureModal(true);
      }
    }, 1500);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
  };

  const handleFailureModalClose = () => {
    setShowFailureModal(false);
  };

  const handleTryAgain = () => {
    setShowFailureModal(false);
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: "" };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength <= 2) return { strength, label: "Weak", color: "#ff3b30" };
    if (strength <= 3) return { strength, label: "Medium", color: "#ff9500" };
    return { strength, label: "Strong", color: "#34c759" };
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const [isOpen, setIsOpen] = useState(false);
  const handleDeactivateAccount = () => {
    navigate("/account/deactivate");
  };

  const handleDeletedAccount = () => {
    navigate("/account/deleted");
  };

  return (
    <>
      <div className="security-section">
        <div className="security-wrapper">
          <div className="security-hamburger" onClick={() => setIsOpen(true)}>
            <span></span>
            <span></span>
            <span></span>
          </div>

          {isOpen && (
            <div
              className="security-modal-overlay"
              onClick={() => setIsOpen(false)}
            >
              <div
                className="security-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="security-close-btn"
                  onClick={() => setIsOpen(false)}
                >
                  ✕
                </button>
                <div className="security-account-activity">
                  Account Activity
                </div>
                <div className="security-deactivate-section">
                  <button
                    className="security-delete-button"
                    onClick={handleDeletedAccount}
                  >
                    <svg
                      className="security-deactivate-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                      aria-hidden="true"
                      style={{ marginRight: "8px" }}
                    >
                      <path d="M9 3h6l1 2h5v2H3V5h5l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9z" />
                    </svg>
                    Delete my account
                  </button>
                </div>

                <div className="security-deactivate-section">
                  <button
                    className="security-deactivate-button"
                    onClick={handleDeactivateAccount}
                  >
                    <span className="security-deactivate-icon">🚫</span>
                    Deactivate my account
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="security-header">
          <h2>Change Password</h2>
          <p className="security-subtitle">
            Ensure your account is using a strong password to stay secure
          </p>
        </div>

        <form onSubmit={handleSubmit} className="password-form">
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <div className="password-input-container">
              <input
                type={showCurrentPassword ? "text" : "password"}
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={errors.currentPassword ? "error" : ""}
                placeholder="Enter your current password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                aria-label={
                  showCurrentPassword ? "Hide password" : "Show password"
                }
              >
                {showCurrentPassword ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="error-message">{errors.currentPassword}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-input-container">
              <input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={errors.newPassword ? "error" : ""}
                placeholder="Enter new password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                {showNewPassword ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {errors.newPassword && (
              <p className="error-message">{errors.newPassword}</p>
            )}

            {newPassword && !errors.newPassword && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div
                    className="strength-progress"
                    style={{
                      width: `${(passwordStrength.strength / 5) * 100}%`,
                      backgroundColor: passwordStrength.color,
                    }}
                  />
                </div>
                <span
                  className="strength-label"
                  style={{ color: passwordStrength.color }}
                >
                  {passwordStrength.label}
                </span>
              </div>
            )}

            <div className="password-requirements">
              <p className="requirements-title">Password must contain:</p>
              <ul className="requirements-list">
                <li className={newPassword.length >= 8 ? "valid" : ""}>
                  At least 8 characters
                </li>
                <li className={/[A-Z]/.test(newPassword) ? "valid" : ""}>
                  One uppercase letter
                </li>
                <li className={/[a-z]/.test(newPassword) ? "valid" : ""}>
                  One lowercase letter
                </li>
                <li className={/[0-9]/.test(newPassword) ? "valid" : ""}>
                  One number
                </li>
                <li
                  className={
                    /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? "valid" : ""
                  }
                >
                  One special character
                </li>
              </ul>
            </div>
          </div>
          <div></div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={errors.confirmPassword ? "error" : ""}
                placeholder="Re-enter your new password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                {showConfirmPassword ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="error-message">{errors.confirmPassword}</p>
            )}
            {confirmPassword &&
              !errors.confirmPassword &&
              newPassword === confirmPassword && (
                <p className="success-text">Passwords match ✓</p>
              )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="change-password-button"
              disabled={loading}
            >
              {loading ? "Changing Password..." : "Change Password"}
            </button>
          </div>
        </form>
      </div>

      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="modal">
            <button
              className="modal-close"
              onClick={handleSuccessModalClose}
              aria-label="Close modal"
            >
              ×
            </button>
            <div className="modal-content">
              <div className="modal-icon success-icon">
                <div className="lock-icon">
                  <img src="/images/lock.png" alt="Success" />
                </div>
              </div>
              <h3 className="modal-title">Updated</h3>
              <p className="modal-message">
                You have successfully updated your password.
              </p>
              <button
                className="modal-button success-button"
                onClick={handleSuccessModalClose}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {showFailureModal && (
        <div className="modal-overlay">
          <div className="modal">
            <button
              className="modal-close"
              onClick={handleFailureModalClose}
              aria-label="Close modal"
            >
              ×
            </button>
            <div className="modal-content">
              <div className="modal-icon failure-icon">
                <div className="shield-icon">
                  <img src="/images/failed.png" alt="Failed" />
                </div>
              </div>
              <h3 className="modal-title">Failed</h3>
              <p className="modal-message">
                Password update failed, try again.
              </p>
              <div className="modal-buttons">
                <button
                  className="modal-button close-button"
                  onClick={handleFailureModalClose}
                >
                  Close
                </button>
                <button
                  className="modal-button try-again-button"
                  onClick={handleTryAgain}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Security;
